/**
 * @fileoverview Generic configuration module for React frontends.
 * Reads from Vite environment variables (import.meta.env.VITE_*).
 * Provides typed getters with sensible development defaults.
 */

/**
 * Determines if we're in development mode.
 */
const isDevelopment = (): boolean => {
  return import.meta.env.DEV || import.meta.env.MODE === 'development';
};

/**
 * Safely reads an environment variable with an optional default.
 * @param key - The environment variable key (without VITE_ prefix)
 * @param defaultValue - Default value if not set
 */
const getEnvVar = (key: string, defaultValue: string = ''): string => {
  const viteKey = `VITE_${key}`;
  const value = import.meta.env[viteKey];
  return value !== undefined ? String(value) : defaultValue;
};

/**
 * Parses a boolean environment variable.
 * @param key - The environment variable key (without VITE_ prefix)
 * @param defaultValue - Default boolean value
 */
const getBoolEnvVar = (key: string, defaultValue: boolean): boolean => {
  const value = getEnvVar(key, '');
  if (value === '') return defaultValue;
  return value === 'true' || value === '1';
};

/**
 * Parses a numeric environment variable.
 * @param key - The environment variable key (without VITE_ prefix)
 * @param defaultValue - Default numeric value
 */
const getNumberEnvVar = (key: string, defaultValue: number): number => {
  const value = getEnvVar(key, '');
  if (value === '') return defaultValue;
  const parsed = Number(value);
  return Number.isNaN(parsed) ? defaultValue : parsed;
};

/**
 * Configuration interface for type safety
 */
export interface AppConfig {
  /** Backend API base URL */
  readonly apiUrl: string;
  /** Current application environment name */
  readonly appEnv: string;
  /** Whether debug mode is enabled */
  readonly isDebug: boolean;
  /** Whether the app is running in development mode */
  readonly isDevelopment: boolean;
  /** Whether the app is running in production mode */
  readonly isProduction: boolean;
  /** Retrieves a custom environment variable by key */
  get(key: string, defaultValue?: string): string;
  /** Retrieves a custom boolean environment variable by key */
  getBool(key: string, defaultValue?: boolean): boolean;
  /** Retrieves a custom numeric environment variable by key */
  getNumber(key: string, defaultValue?: number): number;
  /** Returns all core configuration values */
  getAll(): ConfigValues;
}

export interface ConfigValues {
  apiUrl: string;
  appEnv: string;
  isDebug: boolean;
  isDevelopment: boolean;
  isProduction: boolean;
}

/**
 * Application configuration object.
 * Provides typed access to environment variables with sensible defaults.
 *
 * @example
 * import { config } from '@/core/config';
 *
 * const apiUrl = config.apiUrl;
 * const isDebug = config.isDebug;
 * const env = config.appEnv;
 *
 * // View all config (useful for debugging)
 * console.log(config.getAll());
 */
export const config: AppConfig = {
  /**
   * Backend API base URL.
   */
  get apiUrl(): string {
    return getEnvVar('API_URL', '');
  },

  /**
   * Current application environment name.
   */
  get appEnv(): string {
    return getEnvVar('APP_ENV', 'development');
  },

  /**
   * Whether debug mode is enabled.
   * Defaults to true in development, false otherwise.
   */
  get isDebug(): boolean {
    return getBoolEnvVar('DEBUG', isDevelopment());
  },

  /**
   * Whether the app is running in development mode.
   */
  get isDevelopment(): boolean {
    return isDevelopment();
  },

  /**
   * Whether the app is running in production mode.
   */
  get isProduction(): boolean {
    return import.meta.env.PROD || import.meta.env.MODE === 'production';
  },

  /**
   * Retrieves a custom environment variable by key.
   * Use this for app-specific config that isn't part of the core.
   * @param key - The environment variable key (without VITE_ prefix)
   * @param defaultValue - Default value if not set
   */
  get(key: string, defaultValue: string = ''): string {
    return getEnvVar(key, defaultValue);
  },

  /**
   * Retrieves a custom boolean environment variable by key.
   * @param key - The environment variable key (without VITE_ prefix)
   * @param defaultValue - Default value if not set
   */
  getBool(key: string, defaultValue: boolean = false): boolean {
    return getBoolEnvVar(key, defaultValue);
  },

  /**
   * Retrieves a custom numeric environment variable by key.
   * @param key - The environment variable key (without VITE_ prefix)
   * @param defaultValue - Default value if not set or invalid
   */
  getNumber(key: string, defaultValue: number = 0): number {
    return getNumberEnvVar(key, defaultValue);
  },

  /**
   * Returns all core configuration values.
   * Useful for debugging and logging.
   */
  getAll(): ConfigValues {
    return {
      apiUrl: this.apiUrl,
      appEnv: this.appEnv,
      isDebug: this.isDebug,
      isDevelopment: this.isDevelopment,
      isProduction: this.isProduction,
    };
  },
};

/**
 * Convenience export for the full API base URL including the /api/v1 prefix.
 * Used by AuthProvider, useAuth hook, and generated API client modules (4.4).
 *
 * All backend endpoints live under /api/v1/, so this saves every consumer
 * from appending the prefix manually.
 *
 * The raw base URL (without /api/v1) is available via config.apiUrl.
 *
 * @example
 * import { API_BASE_URL } from '@/core/config';
 *
 * fetch(`${API_BASE_URL}/orders`);  // → http://localhost:8000/api/v1/orders
 */
export const API_BASE_URL = `${config.apiUrl}/api/v1`;

export default config;